// E-signature calls. Signed-in calls go through the app's own request (Entra token); the signer's page and the
// verification page are public: the link is the key, so they use plain fetch with no sign-in.
import { ApiError, request, requestFile, type ContractDetail, type SignatureStatus } from "../api";

export type SigRole = "customer" | "countersigner" | "cc";
export type SigRecipient = {
  id: string; role: SigRole; kind: "external" | "internal"; name: string; email: string; title: string | null; userId: string | null; order: number;
  status: "waiting" | "sent" | "viewed" | "signed" | "declined"; sentAt: string | null; viewedAt: string | null; consentAt: string | null; signedAt: string | null;
  declinedAt: string | null; declineReason: string | null; authMethod: string | null; ip: string | null;
};
export type SigFile = { id: string; kind: "original" | "signed" | "certificate"; name: string; size: number; sha256: string; createdAt: string };
export type SigEvent = { id: number; at: string; type: string; actor: string | null; ip: string | null; detail: string | null; recipientId: string | null };
export type SigRequest = {
  id: string; status: "out" | "finishing" | "completed" | "declined" | "voided" | "expired"; recordVersion: number; title: string; senderName: string; message: string;
  delivery: "email" | "outlook" | "link"; createdAt: string; expiresAt: string; completedAt: string | null; endedAt: string | null; endReason: string | null; lastRemindedAt: string | null;
  originalSha256: string | null; signedSha256: string | null; certificateSha256: string | null;
  recipients: SigRecipient[]; files: SigFile[]; events: SigEvent[]; chain: { ok: boolean; events: number; brokenAt?: number };
};
export type ContractSigning = {
  status: SignatureStatus; executedAt: string | null; signedFileId: string | null; revision: number; contractState: string;
  settings: { requireSignature: boolean; expiryDays: number; reminderDays: number; companyName: string | null };
  delivery: { kind: "email" | "outlook" | "link"; via: string | null };
  current: SigRequest | null;
  previous: { id: string; status: string; recordVersion: number; createdAt: string; endedAt: string | null; endReason: string | null; senderName: string; files: SigFile[] }[];
  canSend: boolean; sendBlocked: string | null;
  defaults: {
    customer: { name: string; email: string; title: string | null; contactId: string } | null;
    contacts: { id: string; name: string; email: string; title: string | null }[];
    countersigner: { userId: string; name: string; email: string | null } | null;
  };
  waitingFor: { name: string; role: SigRole; kind: "external" | "internal" } | null;
  canCountersign: boolean;
};
export type SendBody = {
  customer: { name: string; email: string; title?: string | null; contactId?: string | null };
  countersigner: { userId: string } | null; cc?: { name: string; email: string }[];
  message?: string; expiresInDays?: number; reminderDays?: number;
};
export type SigLink = { name: string; email: string; url: string };
export type SigSettings = { requireSignature: boolean; expiryDays: number; reminderDays: number; companyName: string | null; bcCompanyName: string | null; delivery: { kind: "email" | "outlook" | "link"; via: string | null } };

export type SigFieldType = "signature" | "initials" | "name" | "title" | "date" | "text" | "checkbox";
export type SigField = { id: string; role: SigRole; type: SigFieldType; label: string; page: number; x: number; y: number; width: number; height: number; required: boolean; mine: boolean; value: string | null };
export type SignerView = {
  status: "ready" | "signed" | "waiting" | "copy" | "finishing" | "completed" | "declined" | "voided" | "expired";
  request: { id: string; number: string; title: string; companyName: string; senderName: string; message: string; createdAt: string; expiresAt: string; completedAt: string | null; endReason: string | null; version: number };
  recipient: { id: string; name: string; email: string; title: string | null; role: SigRole; kind: "external" | "internal"; consentAt: string | null; signedAt: string | null };
  signers: { name: string; role: SigRole; status: string; signedAt: string | null }[];
  fields: SigField[];
  documents: { original: string; signed?: string; certificate?: string };
  fingerprints: { original: string | null; signed: string | null };
  consent: string[];
};
export type Ink = { kind: "typed"; text: string } | { kind: "drawn"; image: string };
export type SignBody = { consent?: boolean; ink: Ink; values: Record<string, string | boolean>; timeZone?: string };

const enc = encodeURIComponent;
export const sgApi = {
  signing: (contract: string) => request<ContractSigning>("GET", `/api/contracts/${enc(contract)}/signing`),
  send: (contract: string, body: SendBody) => request<{ signing: ContractSigning; link?: SigLink; emailed: boolean }>("POST", `/api/contracts/${enc(contract)}/signing`, body),
  remind: (contract: string) => request<{ signing: ContractSigning; emailed: boolean }>("POST", `/api/contracts/${enc(contract)}/signing/remind`),
  link: (contract: string) => request<SigLink>("POST", `/api/contracts/${enc(contract)}/signing/link`),
  correct: (contract: string, recipientId: string, body: { name?: string; email: string }) => request<{ signing: ContractSigning; link?: SigLink }>("PATCH", `/api/contracts/${enc(contract)}/signing/recipients/${recipientId}`, body),
  void: (contract: string, reason: string) => request<{ signing: ContractSigning; contract: ContractDetail }>("POST", `/api/contracts/${enc(contract)}/signing/void`, { reason }),
  signView: (contract: string) => request<SignerView>("GET", `/api/contracts/${enc(contract)}/signing/sign-view`),
  countersign: (contract: string, body: SignBody) => request<SignerView>("POST", `/api/contracts/${enc(contract)}/signing/countersign`, body),
  file: (contract: string, fileId: string) => requestFile(`/api/contracts/${enc(contract)}/signing/files/${fileId}`),
  settings: () => request<SigSettings>("GET", "/api/signing/settings"),
  saveSettings: (body: Partial<Pick<SigSettings, "requireSignature" | "expiryDays" | "reminderDays" | "companyName">>) => request<SigSettings>("PUT", "/api/signing/settings", body),
  people: () => request<{ id: string; name: string; email: string | null }[]>("GET", "/api/signing/people"),
};

async function pub<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(path, { method, headers: { "content-type": "application/json" }, body: body === undefined ? undefined : JSON.stringify(body) });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new ApiError(res.status, json.error ?? `Request failed (${res.status})`, json);
  return json as T;
}
export const publicSign = {
  view: (token: string) => pub<SignerView>("GET", `/api/sign/${enc(token)}`),
  consent: (token: string) => pub<{ ok: true }>("POST", `/api/sign/${enc(token)}/consent`),
  sign: (token: string, body: SignBody) => pub<SignerView>("POST", `/api/sign/${enc(token)}/sign`, body),
  decline: (token: string, reason: string) => pub<SignerView>("POST", `/api/sign/${enc(token)}/decline`, { reason }),
  file: async (url: string) => { const r = await fetch(url); if (!r.ok) throw new ApiError(r.status, "The document isn't available.", {}); return r.blob(); },
};
export type VerifyResult = { match: false } | {
  match: true; kind: "original" | "signed" | "certificate"; number: string; revision: number; title: string; company: string; status: string; sentAt: string; completedAt: string | null;
  signers: { name: string; role: SigRole; signedAt: string | null }[]; newer: string | null;
};
export const verify = (sha256: string) => pub<VerifyResult>("POST", "/api/verify", { sha256 });

export async function sha256Hex(blob: Blob): Promise<string> {
  const h = await crypto.subtle.digest("SHA-256", await blob.arrayBuffer());
  return [...new Uint8Array(h)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

/** Open a PDF from a Blob in a new tab (opened first, so the popup isn't blocked while it downloads). */
export async function openPdf(get: () => Promise<Blob>, name?: string, download = false) {
  const w = download ? null : window.open("", "_blank");
  try {
    const url = URL.createObjectURL(new Blob([await get()], { type: "application/pdf" }));
    if (download || !w) { const a = document.createElement("a"); a.href = url; a.download = name ?? "document.pdf"; a.click(); }
    else w.location.href = url;
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (e) { w?.close(); throw e; }
}

export const SIG_STATUS: Record<SignatureStatus, { label: string; tone: "" | "info" | "warn" | "ok" | "danger" }> = {
  not_sent: { label: "Not sent", tone: "" },
  out_for_signature: { label: "Out for signature", tone: "info" },
  partially_signed: { label: "Partially signed", tone: "info" },
  signed: { label: "Signed", tone: "ok" },
  declined: { label: "Declined", tone: "danger" },
  voided: { label: "Voided", tone: "" },
  expired: { label: "Expired", tone: "warn" },
};
export const ROLE_NAME: Record<SigRole, string> = { customer: "Customer", countersigner: "Countersigner", cc: "Copy" };
export const RECIPIENT_STATUS: Record<string, string> = { waiting: "Waiting", sent: "Sent", viewed: "Viewed", signed: "Signed", declined: "Declined" };
export const EVENT_NAME: Record<string, string> = {
  created: "Created", sent: "Sent", delivered: "Email delivered", opened: "Opened the link", viewed: "Viewed", consented: "Agreed to sign electronically",
  signed: "Signed", declined: "Declined", reminded: "Reminder sent", corrected: "Recipient corrected", voided: "Voided", expired: "Expired", completed: "Completed",
  link_shared: "Link shared by the sender", email_failed: "Email not sent",
};
export const shortSha = (s: string | null | undefined) => (s ? `${s.slice(0, 8)}…${s.slice(-6)}` : "");
